import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { getBriefyConfig } from '../lib/briefyConfig';
import BriefyCharacter from './BriefyCharacter';

const IDLE_MS = () => Number(localStorage.getItem('bc_briefy_idle_ms')) || 60000;
const COOLDOWN_MS = 4 * 60 * 1000;
const DISMISS_COOLDOWN_MS = 10 * 60 * 1000;
const CELEBRATE_COOLDOWN_MS = 45 * 1000;
const VISIBLE_MS = 12000;

const MODULES = [
  { prefix: '/finanzas', ask: 'Estoy revisando mis finanzas, ¿qué debería mirar hoy?' },
  { prefix: '/pos', ask: 'Dame un resumen rápido de mis ventas de hoy.' },
  { prefix: '/customers', ask: '¿Qué clientes debería contactar hoy?' },
  { prefix: '/leads', ask: '¿Cómo puedo avanzar mis leads más calientes?' },
  { prefix: '/inventory', ask: '¿Qué productos tienen stock bajo?' },
  { prefix: '/facturas', ask: '¿Tengo facturas pendientes de cobrar?' },
  { prefix: '/tasks', ask: '¿Qué tareas tengo pendientes para hoy?' },
  { prefix: '/payroll', ask: 'Ayúdame con el cálculo de la nómina.' },
  { prefix: '/dashboard', ask: 'Dame un resumen de cómo va mi negocio hoy.' },
];
const getAsk = (path) => (MODULES.find((m) => path.startsWith(m.prefix)) || { ask: '¿En qué me puedes ayudar ahora mismo?' }).ask;

const playPop = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.setValueAtTime(520, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.12);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.07, ctx.currentTime + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.32);
  } catch (e) { /* audio blocked until user gesture — fine */ }
};

const vibrate = (pattern) => {
  if (navigator.vibrate) { try { navigator.vibrate(pattern); } catch (e) {} }
};

export default function BriefyIdle() {
  const { t } = useLanguage();
  const location = useLocation();
  const [phase, setPhase] = useState('hidden'); // hidden | enter | active | leaving
  const [bubbleOn, setBubbleOn] = useState(false);
  const [blinkOn, setBlinkOn] = useState(false);
  const [party, setParty] = useState(null);
  const idleTimer = useRef(null);
  const phaseTimers = useRef([]);
  const blinkTimers = useRef([]);
  const partyTimer = useRef(null);
  const celebrateAt = useRef(0);
  const phaseRef = useRef('hidden');
  const locRef = useRef(location.pathname);
  locRef.current = location.pathname;
  phaseRef.current = phase;

  const clearPhaseTimers = () => { phaseTimers.current.forEach(clearTimeout); phaseTimers.current = []; };
  const clearBlinkTimers = () => { blinkTimers.current.forEach(clearTimeout); blinkTimers.current = []; };
  const after = (ms, fn) => phaseTimers.current.push(setTimeout(fn, ms));

  const doBlink = useCallback(() => {
    setBlinkOn(true);
    blinkTimers.current.push(setTimeout(() => setBlinkOn(false), 160));
  }, []);

  const blinkLoop = useCallback(() => {
    const next = 2600 + Math.random() * 2400;
    blinkTimers.current.push(setTimeout(() => {
      if (phaseRef.current === 'active') { doBlink(); blinkLoop(); }
    }, next));
  }, [doBlink]);

  const leave = useCallback((cooldown = COOLDOWN_MS) => {
    clearPhaseTimers();
    clearBlinkTimers();
    localStorage.setItem('bc_briefy_cooldown', String(Date.now() + cooldown));
    setBubbleOn(false);
    setPhase('leaving');
    phaseTimers.current.push(setTimeout(() => { setPhase('hidden'); setBlinkOn(false); }, 750));
  }, []);

  const appear = useCallback(() => {
    const cfg = getBriefyConfig();
    if (!cfg.interactive || !cfg.idle) return;
    if (document.hidden || window.__briefyChatOpen) return;
    if (Date.now() < Number(localStorage.getItem('bc_briefy_cooldown') || 0)) return;
    if (phaseRef.current !== 'hidden') return;

    if (cfg.haptics) vibrate(12); // una sola vibración corta
    if (cfg.sound) playPop();
    setBlinkOn(false);
    setBubbleOn(false);
    setPhase('enter');
    after(950, () => { setPhase('active'); blinkLoop(); });
    after(1250, () => setBubbleOn(true));
    // despedida: un último parpadeo, sonríe y se retira por el mismo lateral
    after(VISIBLE_MS - 700, () => doBlink());
    after(VISIBLE_MS, () => leave(COOLDOWN_MS));
  }, [blinkLoop, doBlink, leave]);

  // Detección de inactividad — cualquier interacción reinicia el contador
  useEffect(() => {
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (document.hidden) return;
      idleTimer.current = setTimeout(appear, IDLE_MS());
    };
    let lastMove = 0;
    const onMove = () => { const n = Date.now(); if (n - lastMove > 2000) { lastMove = n; reset(); } };
    const onVis = () => { if (document.hidden) { if (idleTimer.current) clearTimeout(idleTimer.current); } else reset(); };
    const evs = ['pointerdown', 'keydown', 'touchstart', 'wheel'];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    window.addEventListener('mousemove', onMove, { passive: true });
    window.addEventListener('scroll', reset, { passive: true, capture: true });
    document.addEventListener('visibilitychange', onVis);
    reset();
    return () => {
      evs.forEach((e) => window.removeEventListener(e, reset));
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('scroll', reset, { capture: true });
      document.removeEventListener('visibilitychange', onVis);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      clearPhaseTimers();
      clearBlinkTimers();
    };
  }, [appear]);

  // Precarga de los assets usados (peek + parpadeo + celebración)
  useEffect(() => {
    const tm = setTimeout(() => {
      ['/briefy/peek.png', '/briefy/peek_blink.png', '/briefy/happy.png', '/briefy/explaining.png'].forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    }, 6000);
    return () => clearTimeout(tm);
  }, []);

  // Celebración: evento global al completar operaciones (p. ej. venta POS)
  useEffect(() => {
    const onParty = (e) => {
      const cfg = getBriefyConfig();
      if (!cfg.interactive) return;
      if (Date.now() - celebrateAt.current < CELEBRATE_COOLDOWN_MS) return;
      celebrateAt.current = Date.now();
      if (phaseRef.current !== 'hidden') leave(COOLDOWN_MS);
      if (cfg.haptics) vibrate([15, 60, 20]);
      setParty({ msg: e.detail?.msg || '¡Bien hecho! 🎉' });
      if (partyTimer.current) clearTimeout(partyTimer.current);
      partyTimer.current = setTimeout(() => setParty(null), 3200);
    };
    window.addEventListener('briefy:celebrate', onParty);
    return () => { window.removeEventListener('briefy:celebrate', onParty); if (partyTimer.current) clearTimeout(partyTimer.current); };
  }, [leave]);

  const cfg = getBriefyConfig();

  const openAssistant = () => {
    leave(DISMISS_COOLDOWN_MS);
    window.dispatchEvent(new CustomEvent('briefy:open', { detail: { prefill: getAsk(locRef.current) } }));
  };

  const animCls = cfg.animations
    ? (phase === 'enter' ? 'briefy-peek-in2' : phase === 'leaving' ? 'briefy-peek-out2' : 'briefy-peek-hold2')
    : `briefy-peek-hold2 transition-opacity duration-500 ${phase === 'leaving' ? 'opacity-0' : ''}`;

  return (
    <>
      {phase !== 'hidden' && (
        <div className="fixed bottom-0 right-0 z-[95] flex flex-col items-end pointer-events-none" data-testid="briefy-idle">
          {bubbleOn && (
            <button
              onClick={openAssistant}
              data-testid="briefy-idle-bubble"
              className={`pointer-events-auto mr-3 mb-1 max-w-[240px] text-left bg-white border border-[#BFDBFE] rounded-2xl rounded-br-sm shadow-xl px-4 py-2.5 cursor-pointer hover:shadow-2xl transition-shadow ${cfg.animations ? 'bubble-soft-in' : ''}`}
            >
              <p className="text-sm font-bold text-[#0B1533]">{t('briefyIdle.g0', '¿Te puedo ayudar en algo?')}</p>
            </button>
          )}
          <button
            onClick={openAssistant}
            data-testid="briefy-idle-mascot"
            aria-label="Briefy"
            className={`pointer-events-auto block drop-shadow-2xl ${animCls}`}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            <div className={cfg.animations ? 'briefy-float-sub' : ''}>
              <div className="relative" style={{ height: 'clamp(240px, 42vh, 420px)', aspectRatio: '397 / 760' }}>
                <img src="/briefy/peek.png" alt="Briefy" draggable="false" className="absolute inset-0 w-full h-full object-contain select-none" />
                <img
                  src="/briefy/peek_blink.png"
                  alt=""
                  aria-hidden="true"
                  draggable="false"
                  className="absolute inset-0 w-full h-full object-contain select-none"
                  style={{ opacity: blinkOn ? 1 : 0, transition: 'opacity 0.1s ease' }}
                />
              </div>
            </div>
          </button>
        </div>
      )}
      {party && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[96] pointer-events-none flex flex-col items-center" data-testid="briefy-celebrate">
          <div className={`${cfg.animations ? 'bubble-pop' : ''} bg-white border border-[#BBF7D0] rounded-2xl shadow-xl px-4 py-2 mb-1`}>
            <p className="text-sm font-extrabold text-[#166534]">{party.msg}</p>
          </div>
          <div className={cfg.animations ? 'briefy-celebrate-pop' : ''}>
            <div className={cfg.animations ? 'briefy-hop' : ''}>
              <BriefyCharacter pose="happy" height="clamp(190px, 32vh, 320px)" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
